import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, useDisclosure, useToast, Box, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { format } from "date-fns";
import DatePicker from "react-datepicker";

import PaidIcon from "@/assets/PaidIcon";
import UnpaidIcon from "@/assets/UnpaidIcon";
import RejectIcon from "@/assets/RejectIcon";
import ApprovedIcon from "@/assets/ApprovedIcon";
import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, CURRENCY, DURATION_TOAST, FORMAT_ADD_DATE, ROLES } from "@/common/constants";
import { FaTrash } from "react-icons/fa";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import FileUploader from "@/components/Fields/FileUploader";
import Loader from "@/components/Layouts/Loader";
import { useAppSelector } from "@/redux/hooks";
import { useDispatch } from "react-redux";
import { setDataMultiplePayment } from "@/redux/slices/paymentSlice";
import { RootState } from "@/redux/store";
import { getInvoiceInspectorById, updatePaymentStatusInspector } from "@/services/invoicesInspectorService";
import { updatePaymentStatusShipOwner } from "@/services/invoicesShipOwnerService";
import { invoicePaymentSchema } from "@/schemas/invoicePaymentSchema";
import * as Yup from "yup";
import { formatCurrency, getCurrencySymbol } from "@/utils/currency";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import { getExchangeRateDateRange} from '@/services/exchangeRateService';

const MultiplePaymentConfirmation = () => {
        const dispatch = useDispatch();
    const dataRaw = useAppSelector((state: RootState) => state.payment.multiplePayment);
    const [showWarning, setShowWarning] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        console.log('dataRaw', dataRaw);
        if (!dataRaw || !dataRaw.data || dataRaw.data.length === 0) {
            setShowWarning(true);
        }
    }, [dataRaw]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const name = useAppSelector((state: RootState) => state.account.name);

    const { type } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState<string>("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState<string>("");
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [paymentFile, setPaymentFile] = useState<Blob | string>('');
    const [title, setTitle] = useState<string>("");
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [amountIDR, setAmountIDR] = useState<number>(0);

    const [latestExchangeRate, setLatestExchangeRate] = useState<any>({});

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Confirm Payment?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = (data: any) => {
        setTitle("Confirm Payment?");
        setConfirmationTest("Are you sure you want to confirm?");
        setConfirmationTitleButton("Confirm Payment");
        onOpen();
        setDataEdit(data);
    }

    const handleConfirm = async () => {
        onClose();
        setIsSubmitting(true);

        if (title === "Cancel to Confirm Payment?") {
            if (role === 'super admin') {
                navigate(type === 'ship operator' ? '/manage-invoices/ship-operator' : '/manage-invoices/inspecting-company');
            } else {
                if (type === 'inspector') {
                    navigate('/manage-invoices/inspecting-company');
                    return;
                }
                navigate('/manage-invoices-ship-operator');
            }
            return;
        }

        const listPromise: Promise<any>[] = [];
        const postIndexes: number[] = [];
        if (dataRaw?.data && Array.isArray(dataRaw.data)) {
            dataRaw.data.forEach((item: any, idx: number) => {
                if (item.paymentStatus !== 1) {
                    const data = new FormData();
                    data.append('invoiceId', item.id);
                    data.append('paymentStatus', '1');
                    data.append('paymentDate', dataEdit ? format(dataEdit.paymentDate, FORMAT_ADD_DATE) : '');
                    data.append('totalAmountIDR', String(amountIDR));
                    data.append('modifiedBy', name || 'System');
                    data.append('file', paymentFile);
                    if (type === 'ship owner') {
                        listPromise.push(updatePaymentStatusShipOwner(data));
                    } else if (type === 'inspector') {
                        listPromise.push(updatePaymentStatusInspector(data));
                    }
                    postIndexes.push(idx);
                }
            });
        }

        const responses = await Promise.all(listPromise);
        const results = responses.map(response => response.status);
        // Create a new array for updated paymentStatus
        const updatedData = dataRaw?.data?.map((item: any, idx: number) => {
            const postIdx = postIndexes.indexOf(idx);
            if (postIdx !== -1 && results[postIdx] === HttpStatusCode.Ok) {
                return { ...item, paymentStatus: 1 };
            }
            return item;
        });
        // Dispatch Redux action to update state
        if (updatedData) {
            dispatch(setDataMultiplePayment({ ...dataRaw, data: updatedData }));
        }

        const allStatusAre200 = results.length > 0 && results.every((item: any) => item === HttpStatusCode.Ok);
        setIsSubmitting(false);
        if (allStatusAre200) {
            // clear all
            dispatch(setDataMultiplePayment({ id: '', data: [] }))
            toast({
                title: `${results.length} invoice status updated.`,
                description: `We've updated ${results.length} invoice status for you.`,
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setTimeout(() => {
                if (role === 'super admin') {
                    navigate(type === 'ship owner' ? '/manage-invoices/ship-operator' : '/manage-invoices/inspecting-company');
                } else {
                    if (type === 'inspector') {
                        navigate('/manage-invoices/inspecting-company');
                        return;
                    }
                    navigate('/manage-invoices-ship-operator');
                }
            }, 1000);
        } else {
            toast({
                title: `Invoice status failed to update`,
                description: `We failed to update invoice status for you.`,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDrop = (acceptedFiles: File[], setFieldValue: any) => {
        setFieldValue('paymentFile', acceptedFiles[0]);
        setPaymentFile(acceptedFiles[0]);
    };

    const getDataDetailInspector = async () => {
        setIsLoading(true);
        const response = await getInvoiceInspectorById(dataRaw.data[0]?.id) as any;
        if (response) {
            setDataDetail(response.data);
        }
        setIsLoading(false);
    }

    const getLatestExchangeRate = async () => {
        setIsLoading(true);
        const end = format(new Date(new Date().setDate(new Date().getDate() - 1)), 'yyyy-MM-dd');
        const start = format(new Date(new Date().setDate(new Date().getDate() - 15)), 'yyyy-MM-dd');
        const response = await getExchangeRateDateRange(start, end, 1, 10);
                    if (response.status === HttpStatusCode.Ok && response.data) {
                        console.log('response exchange rate', response.data[0]);
                        setLatestExchangeRate(response.data[0]);
                    }
        setIsLoading(false);
    }


    useEffect(() => {
        if (dataRaw && dataRaw.data && dataRaw.data.length > 0) {
            getDataDetailInspector();
            getLatestExchangeRate();
        }
    }, []);



    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                amount: type === 'ship owner'
                                    ? (dataDetail?.isKursConversion && dataDetail?.mataUang
                                        ? getCurrencySymbol(dataDetail?.mataUang, dataDetail?.conversionAmount)
                                        : (() => {
                                            const total = dataRaw?.data?.reduce((acc: number, item: any) => acc + (item.amount || 0), 0) || 0;
                                            const currency = dataRaw?.data?.[0]?.currency || 'IDR';
                                            return formatCurrency(total, currency, currency === 'IDR' ? 'id-ID' : 'en-US');
                                        })())
                                    : (() => {
                                        const total = dataRaw?.data?.reduce((acc: number, item: any) => acc + (item.amount || 0), 0) || 0;
                                        const currency = dataRaw?.data?.[0]?.currency || 'IDR';
                                        return formatCurrency(total, currency, currency === 'IDR' ? 'id-ID' : 'en-US');
                                    })(),
                                amountIDR: 0,
                                paymentFile: null,
                                paymentDate: dataRaw?.data?.[0]?.invoiceDate ? new Date(dataRaw.data[0].invoiceDate) : null
                            }}
                            validationSchema={Yup.object({
                                ...invoicePaymentSchema(role).fields,
                                amountIDR: Yup.number()
                                    .required('Amount in IDR is required')
                                    .test('min-amount', 'Amount in IDR must be greater than or equal to total amount', function(value) {
                                        // Calculate total amount
                                        const total = dataRaw?.data?.reduce((acc: number, item: any) => acc + (item.amount || 0), 0) || 0;
                                        return typeof value === 'number' && value >= total;
                                    }),
                            })}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>

                                    {showWarning ? (
                                        <Box bg="yellow.100" p={6} borderRadius={8} textAlign="center" mt={8} mb={8}>
                                            <div style={{ fontWeight: 'bold', color: '#b7791f', fontSize: 18, marginBottom: 12 }}>
                                                Warning: No payment data found.
                                            </div>
                                            <Button colorScheme="yellow" onClick={() => navigate('/manage-invoices/inspecting-company/')}>Back</Button>
                                        </Box>
                                    ) : (
                                        <>
                                            <Field name='paymentDate'>
                                                {({ field, form }: any) => (
                                                    <FormControl isInvalid={form.errors.paymentDate && form.touched.paymentDate} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">Payment Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                                            <div style={{
                                                                width: '100%',
                                                                height: '2.5rem',
                                                                fontSize: '1rem',
                                                                paddingInlineStart: '1rem',
                                                                paddingInlineEnd: '1rem',
                                                                borderRadius: '0.375rem',
                                                                minWidth: '0px',
                                                                outline: '2px solid transparent',
                                                                outlineOffset: '2px',
                                                                position: 'relative',
                                                                appearance: 'none',
                                                                transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                                paddingTop: '0.35rem',
                                                                marginBottom: (form.errors.paymentDate && form.touched.paymentDate) ? '1.25rem' : 0,
                                                                border: form.errors.paymentDate && form.touched.paymentDate ? '2px solid red' : '1px solid #e5e7e9'
                                                            }}>
                                                                <DatePicker
                                                                    id="paymentDate"
                                                                    selected={field.value as Date | null}
                                                                    onChange={(date: Date | null) => props.setFieldValue('paymentDate', date)}
                                                                    dateFormat="dd-MM-YYYY"
                                                                    placeholderText="Payment Date"
                                                                    readOnly={isSubmitting}
                                                                />
                                                                <FormErrorMessage>{form.errors.paymentDate}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <Field name='amount'>
                                                {({ field, form }: any) => (
                                                    <FormControl isInvalid={form.errors.amount && form.touched.amount} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">Total Amount<span style={{ color: 'red' }}>*</span></FormLabel>
                                                            <div className="form-input-container">
                                                                <Input {...field} disabled placeholder='Amount' className="form-input" />
                                                                <FormErrorMessage>{form.errors.amount}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                    </FormControl>
                                                )}
                                            </Field>
                                            {role === ROLES.FINANCE && <Field name='amountIDR'>
                                                {({ form }: any) => (
                                                    <>
                                                        <FormControl isInvalid={form.errors.amountIDR && form.touched.amountIDR} style={{ marginBottom: 15 }}>
                                                            <div className="form-field-container">
                                                                <FormLabel className="form-label">Amount in IDR<span style={{ color: 'red' }}>*</span></FormLabel>
                                                                <div className="form-input-container">
                                                                    <CurrencyInput
                                                                        placeholder='Input Conversion Amount'
                                                                        prefix={CURRENCY.IDR.prefix}
                                                                        value={typeof amountIDR === 'number' ? amountIDR : 0}
                                                                        defaultValue={typeof props.values.amountIDR === 'number' ? props.values.amountIDR : 0}
                                                                        error={form.errors.amountIDR && form.touched.amountIDR}
                                                                        onValueChange={val => {
                                                                            let value = 0;
                                                                            if (val && typeof val.floatValue === 'number' && !isNaN(val.floatValue)) {
                                                                                value = val.floatValue;
                                                                            } else if (val && typeof val.value === 'string') {
                                                                                // Remove non-numeric characters and parse
                                                                                const sanitized = val.value.replace(/[^0-9]/g, "");
                                                                                value = sanitized ? Number(sanitized) : 0;
                                                                            }
                                                                            // Always set as number
                                                                            props.setFieldValue('amountIDR', value);
                                                                            setAmountIDR(value);
                                                                        }}
                                                                        width="100%"
                                                                    />
                                                                    <FormErrorMessage>{form.errors.amountIDR}</FormErrorMessage>
                                                                </div>
                                                            </div>
                                                        </FormControl>
                                                        {/* Kurs field and calculate button */}
                                                        <FormControl style={{ marginBottom: 15 }}>
                                                            <div className="form-field-container">
                                                                <FormLabel className="form-label">Latest Exchange Rate</FormLabel>
                                                                <div className="form-input-container" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexDirection: 'row' }}>
                                                                    <Input
                                                                        name="kurs"
                                                                        value={form.values.kurs ?? latestExchangeRate?.kurs ?? ''}
                                                                        onChange={e => {
                                                                            const val = e.target.value;
                                                                            form.setFieldValue('kurs', val);
                                                                        }}
                                                                        placeholder="Latest Exchange Rate"
                                                                        style={{ maxWidth: `calc(100% - 200px)` }}
                                                                    />
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            const currency = dataRaw?.data?.[0]?.currency || 'IDR';
                                                                            if (currency !== 'IDR') {
                                                                                let amountValue = 0;
                                                                                if (typeof form.values.amount === 'string') {
                                                                                    amountValue = Number(form.values.amount.replace(/[^0-9.-]+/g, ""));
                                                                                } else {
                                                                                    amountValue = Number(form.values.amount) || 0;
                                                                                }
                                                                                const rate = Number(form.values.kurs ?? latestExchangeRate?.kurs) || 0;
                                                                                const calculated = amountValue * rate;
                                                                                form.setFieldValue('amountIDR', calculated);
                                                                                setAmountIDR(calculated);
                                                                            }
                                                                        }}
                                                                        disabled={dataRaw?.data?.[0]?.currency === 'IDR' || !latestExchangeRate?.kurs}
                                                                    >
                                                                        Calculate Amount in IDR
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </FormControl>
                                                    </>
                                                )}
                                            </Field>}
                                            <Field name='paymentFile'>
                                                {({ form }: any) => (
                                                    <FormControl isInvalid={form.errors.paymentFile && form.submitCount} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">Payment File<span style={{ color: 'red' }}>*</span></FormLabel>
                                                            <div className="form-input-container">
                                                                <FileUploader onDrop={(e) => handleDrop(e, props.setFieldValue)} isInvalid={form.errors.paymentFile && form.submitCount} isDisabled={props.isSubmitting} type='supporting-files' />
                                                                <FormErrorMessage>{form.errors.paymentFile}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <FormLabel className="form-label">Invoice List</FormLabel>
                                             <TableContainer bgColor="#fff" borderRadius="13" overflowX="scroll" border="1px solid #eef0f2" marginTop='20px'>
                                                <Box overflowX="auto">
                                                    <Table variant='simple'>
                                                        <Thead>
                                                            <Tr>
                                                                <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Invoice ID</Th>
                                                                <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Invoice Date</Th>
                                                                <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Vessel Name</Th>
                                                                <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Company Name</Th>
                                                                <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Amount</Th>
                                                                <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Payment Status</Th>
                                                                <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Delete</Th>
                                                            </Tr>
                                                        </Thead>
                                                        <Tbody>
                                                            {!dataRaw?.data || dataRaw?.data?.length === 0 &&
                                                                <Tr>
                                                                    <Td colSpan={5} textAlign="center">Data not found</Td>
                                                                </Tr>
                                                            }
                                                            {dataRaw?.data?.map((item: any, idx: number) => {
                                                                return (
                                                                    <Tr key={item.invoiceNumber}>
                                                                        <Td>{item.invoiceNumber}</Td>
                                                                        <Td>{item.invoiceDate ? format(item.invoiceDate, 'dd-MM-yyyy') : '-'}</Td>
                                                                        <Td>{item.shipName}</Td>
                                                                        <Td>{item.companyName}</Td>
                                                                        <Td>{formatCurrency(item.amount, item.currency, item.currency == 'IDR' ? 'id-ID' : 'en-US')}</Td>
                                                                        <Td textAlign='center'>
                                                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                                                {item.paymentStatus === 0 && <UnpaidIcon />}
                                                                                {item.paymentStatus === 1 && <PaidIcon />}
                                                                                {item.paymentStatus === 2 && <ApprovedIcon />}
                                                                                {item.paymentStatus === 3 && <RejectIcon />}
                                                                                {item.paymentStatus === 4 && 'Completed'}
                                                                            </div>
                                                                        </Td>
                                                                        <Td textAlign='center'>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    const newData = dataRaw.data.filter((_: any, i: number) => i !== idx);
                                                                                    dispatch(setDataMultiplePayment({ ...dataRaw, data: [...newData] }));
                                                                                }}
                                                                                aria-label="Delete row"
                                                                            >
                                                                                <FaTrash color="#E53E3E" />
                                                                            </Button>
                                                                        </Td>
                                                                    </Tr>
                                                                )
                                                            })}
                                                        </Tbody>
                                                    </Table>
                                                </Box>
                                            </TableContainer>
                                        </>
                                    )}
                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            loadingText='Processing...'
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                            disabled={showWarning || isSubmitting}
                                        >
                                            Confirm Payment
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default MultiplePaymentConfirmation;